<div class="flex h-screen min-h-0 flex-col gap-4 overflow-hidden p-4">
  <p-toast />
  <div class="shrink-0">
    <h1 class="text-xl font-semibold text-slate-900">
      {{ 'price_history.title' | translate }}
    </h1>
    <p class="mt-1 text-sm text-slate-600">
      {{ 'price_history.subtitle' | translate }}
    </p>
  </div>

  @if (error()) {
    <p role="alert" class="shrink-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {{ error()! | translate }}
    </p>
  }

  <section class="price-search shrink-0" aria-labelledby="price-history-search-title">
    <h2 id="price-history-search-title" class="price-search-title">
      <i class="pi pi-search" aria-hidden="true"></i>
      {{ 'price_list.filters.title' | translate }}
    </h2>
    <form class="price-filters price-filters--history" [formGroup]="form" (ngSubmit)="$event.preventDefault()">
      <label class="text-sm text-slate-700" for="history-sku-name">
        {{ 'price_list.filters.sku_name' | translate }}
        <input
          id="history-sku-name"
          pInputText
          class="w-full"
          type="search"
          formControlName="skuName"
          [attr.placeholder]="'price_list.filters.sku_name_placeholder' | translate"
        />
      </label>
      <label class="text-sm text-slate-700" for="history-sku-code">
        {{ 'price_list.filters.sku_code' | translate }}
        <input
          id="history-sku-code"
          pInputText
          class="w-full"
          type="search"
          formControlName="skuCode"
          [attr.placeholder]="'price_list.filters.sku_code_placeholder' | translate"
        />
      </label>
      <label class="text-sm text-slate-700" for="history-actor">
        {{ 'price_list.history_actor' | translate }}
        <input
          id="history-actor"
          pInputText
          class="w-full"
          type="search"
          formControlName="actor"
          [attr.placeholder]="'price_history.actor_placeholder' | translate"
        />
      </label>
      <label class="text-sm text-slate-700">
        {{ 'price_list.filters.start_date' | translate }}
        <p-datepicker
          formControlName="startDate"
          dateFormat="dd/mm/yy"
          placeholder="dd/mm/yyyy"
          [showIcon]="true"
          styleClass="w-full"
        />
      </label>
      <label class="text-sm text-slate-700">
        {{ 'price_list.filters.end_date' | translate }}
        <p-datepicker
          formControlName="endDate"
          dateFormat="dd/mm/yy"
          placeholder="dd/mm/yyyy"
          [showIcon]="true"
          styleClass="w-full"
        />
      </label>
      <div class="price-filter-actions">
        <button
          type="button"
          class="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          (click)="clearSearch()"
        >
          {{ 'price_list.clear' | translate }}
        </button>
        <button
          type="button"
          class="cursor-pointer rounded-md bg-blue-800 px-3 py-2 text-sm font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          [disabled]="exporting()"
          (click)="exportExcel()"
        >
          {{ 'price_history.export_excel' | translate }}
        </button>
      </div>
    </form>
  </section>

  <div
    class="flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200"
    role="tablist"
    [attr.aria-label]="'price_list.tabs_label' | translate"
  >
    @for (tab of tabs; track tab.code; let index = $index) {
      <button
        type="button"
        role="tab"
        class="cursor-pointer shrink-0 border-b-2 px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
        [class.border-blue-800]="activeCode() === tab.code"
        [class.text-blue-900]="activeCode() === tab.code"
        [class.font-semibold]="activeCode() === tab.code"
        [class.border-transparent]="activeCode() !== tab.code"
        [class.text-slate-600]="activeCode() !== tab.code"
        [attr.aria-selected]="activeCode() === tab.code"
        [attr.tabindex]="activeCode() === tab.code ? 0 : -1"
        (click)="selectTab(tab.code)"
        (keydown)="onTabKeydown($event, index)"
      >
        {{ tab.labelKey | translate }}
      </button>
    }
  </div>

  <div class="min-h-0 flex-1 overflow-auto rounded-md bg-white">
    <table class="price-table w-full min-w-[960px] text-left text-sm">
      <thead>
        <tr>
          <th class="px-3 py-2 font-medium">{{ 'price_history.created' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.sku' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_history.book' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.type' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.change_reason' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.history_actor' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.history_action' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.history_old' | translate }}</th>
          <th class="px-3 py-2 font-medium">{{ 'price_list.history_new' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        @if (loading()) {
          <tr>
            <td colspan="9" class="px-3 py-6 text-slate-600">
              {{ 'price_list.loading' | translate }}
            </td>
          </tr>
        } @else if (items().length === 0) {
          <tr>
            <td colspan="9" class="px-3 py-6 text-center text-slate-600">
              {{ 'price_history.empty' | translate }}
            </td>
          </tr>
        } @else {
          @for (row of items(); track row.id) {
            <tr>
              <td class="px-3 py-2 whitespace-nowrap">
                {{ (row.creationDate || row.changedAt) | date: 'dd/MM/yyyy HH:mm' }}
              </td>
              <td class="px-3 py-2 font-medium text-slate-900">{{ row.skuCode }}</td>
              <td class="px-3 py-2">{{ row.priceBookCode }}</td>
              <td class="px-3 py-2">
                {{ row.priceType ? (('price_list.types.' + row.priceType) | translate) : '' }}
              </td>
              <td class="px-3 py-2">
                @if (row.changeReason) {
                  {{ reasonLabel(row.changeReason) | translate }}
                } @else {
                  —
                }
              </td>
              <td class="px-3 py-2">{{ row.actor }}</td>
              <td class="px-3 py-2">
                {{ ('price_list.history_actions.' + row.action) | translate }}
              </td>
              <td class="px-3 py-2 tabular-nums">
                {{
                  row.oldAmount == null
                    ? ('price_list.no_price' | translate)
                    : (row.oldAmount | number: '1.0-0')
                }}
              </td>
              <td class="px-3 py-2 tabular-nums">
                {{
                  row.newAmount == null
                    ? ('price_list.no_price' | translate)
                    : (row.newAmount | number: '1.0-0')
                }}
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  @if (total() > 0) {
    <div class="flex shrink-0 items-center justify-end gap-4 text-sm text-gray-400">
      <p-paginator
        [rows]="pageSize()"
        [first]="page() * pageSize()"
        [totalRecords]="total()"
        [rowsPerPageOptions]="rowsPerPageOptions"
        [showCurrentPageReport]="true"
        [currentPageReportTemplate]="
          'common.table.pagination.current_page_report' | translate
        "
        (onPageChange)="onTablePage($event)"
        class="p-paginator"
        [appendTo]="'body'"
      />
      <div class="mr-4 flex items-center gap-2">
        <span class="text-sm text-gray-500">{{
          'common.table.pagination.go' | translate
        }}</span>
        <input
          type="number"
          [ngModel]="goToPageNumber()"
          (ngModelChange)="goToPageNumber.set($event)"
          [min]="1"
          [max]="maxPage()"
          (keyup.enter)="onGoToPage()"
          (blur)="onGoToPage()"
          class="h-9 w-16 rounded border px-2 text-center"
        />
        <span class="text-sm text-gray-500">{{
          'common.table.pagination.page' | translate
        }}</span>
      </div>
    </div>
  }
</div>
